/**
 * 내 구독 조회·변경 훅 — 화면(MySubscriptionPage)과 배너(SubscriptionExpiryBanner)가 같은 쿼리 키를 공유한다.
 * 'silent' 모드(배너)는 조회 실패를 "구독 정보 없음"으로 간주해 조용히 숨긴다(토스트·리다이렉트 없음).
 * 'redirect' 모드(페이지)는 오류 문구를 인라인으로 노출한다(인증·기능은 RequireWorkspaceMember·RequireRouteFeature 가드가 이미 보장).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { cancelSubscription, getPaymentMethod, resumeSubscription } from '@/api/billing';
import { getMySubscription } from '@/api/subscription';
import { useFeature } from '@/hooks/useFeature';
import { subscriptionKeys } from '@/lib/queryKeys';

import type { CancelSubscriptionRequest } from '../types';

export type UseMySubscriptionMode = 'redirect' | 'silent';

export function useMySubscription(mode: UseMySubscriptionMode = 'redirect') {
  // 구독 기능이 없는 회원(기업, specs/065 — 1단계 무료)은 묻지 않는다. 배너는 데이터가 없어 그리지 않는다.
  const enabled = useFeature('SUBSCRIPTION');
  const query = useQuery({
    enabled,
    queryKey: subscriptionKeys.me,
    queryFn: () => getMySubscription(mode === 'silent'),
  });
  return {
    data: mode === 'silent' && query.isError ? undefined : query.data,
    isLoading: query.isLoading,
    error: mode === 'silent' || !query.isError ? null : '구독 정보를 조회하지 못했습니다.',
    refetch: query.refetch,
  };
}

/** 해지 — 성공 시 구독·결제수단 캐시 무효화. 토스트는 호출부가 결과(환불액)에 따라 띄운다. */
export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CancelSubscriptionRequest) => cancelSubscription(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: subscriptionKeys.all }),
  });
}

/** 해지 구독 재개(자동갱신 ON) — 응답을 곧바로 캐시에 반영. */
export function useResumeSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => resumeSubscription(),
    onSuccess: (data) => {
      qc.setQueryData(subscriptionKeys.me, data);
      void qc.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

/** 결제수단 조회 — ACTIVE/PAST_DUE 카드에서만 호출(enabled). */
export function usePaymentMethod(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.paymentMethod,
    queryFn: getPaymentMethod,
    enabled,
  });
}
